import React from "react";
import DatePartsInput from "../../components/form/DatePartsInput";

/**
 * `renderInputField`
 * ------------------
 * Pure factory that returns the correct `<input>` / `<select>` element
 * for a questionnaire *question* definition.
 *
 * @param {Object}          params
 * @param {Object}          params.question     Full question object
 * @param {*}               params.value        Current answer for this question
 * @param {Function}        params.onChange     Shared `onChange` handler
 * @param {Function}        [params.onEnter]    Called when user presses Enter
 * @param {React.RefObject} params.inputRef     Ref so parent can focus the field
 *
 * @returns {JSX.Element}  A ready-to-render React element
 *
 * Supported `question.fieldType` values:
 *   • "text"   – default fallback
 *   • "number"
 *   • "date"
 *   • "select" – requires `question.options = [{ value, label }]`
 *   • "range"  – requires `question.min`, `question.max`, optional `step`
 *
 *  This helper is **pure**: no React hooks, no side-effects.
 *  Safe to unit-test in isolation or move to a util module.
 */
export default function renderInputField({
  question,
  value,
  onChange,
  onEnter,
  inputRef,
}) {
  // Base props shared by every field type -----------------------------
  // These ensure consistent styling, Enter-key behaviour, and ref wiring.
  const common = {
    id: question.id,
    name: question.id,
    placeholder: question.title,
    className: "custom-input w-full appearance-none",
    value: value ?? "",
    onChange,
    onKeyDown: (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onEnter?.(e);
      }
    },
    required: question.required,
    ref: inputRef,
  };

  switch (question.fieldType) {
    case "number":
      // ----- number -----
      return <input type="number" {...common} />;
    case "date": {
      return (
        <DatePartsInput
          id={question.id}
          value={value}
          onChange={onChange}
          required={question.required}
        />
      );
    }
    case "select":
      // ----- select -----
      return (
        <select {...common}>
          <option value="">{question.title}</option>
          {question.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    case "range":
      // ----- range -----
      return (
        <div className="range-input-container">
          <input
            type="range"
            {...common}
            className="custom-range w-full"
            value={value ?? question.min}
            min={question.min}
            max={question.max}
            step={question.step || 1}
          />
          <div className="range-values text-xs flex justify-between">
            <span>{question.min}</span>
            <span className="muted-text">{value ?? question.min}</span>
            <span>{question.max}</span>
          </div>
          {question.description && (
            <p className="text-xs text-gray-600">{question.description}</p>
          )}
        </div>
      );

    /* ──────────────  multi-select (checkbox list) ───────────── */
    case "multiselect": {
      /* the parent passes `value` as an array of ids */
      const selected = new Set(value ?? []);

      /* toggle one id on/off */
      const toggle = (id) => () => {
        const next = selected.has(id)
          ? value.filter((x) => x !== id) // remove
          : [...value, id]; // add
        onChange(next);
      };

      /* “add new group +” fires the sentinel */
      const addNew = () => {
        /* push the magic value so the parent handler can open its modal */
        const next = [...(value ?? []), "__new"];
        onChange(next);
      };

      return (
        <div className="flex flex-col gap-2">
          {/* list of checkboxes */}
          <div className="flex flex-col gap-1">
            {question.options
              .filter((opt) => opt.value !== "__new") // keep real groups here
              .map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-center gap-2 cursor-pointer select-none"
                >
                  <input
                    type="checkbox"
                    className="accent-primary"
                    checked={selected.has(opt.value)}
                    onChange={toggle(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
          </div>

          {/* add-new button */}
          <button
            type="button"
            className="small-button self-end"
            onClick={addNew}
          >
            Add new group
          </button>
        </div>
      );
    }
    case "textarea":
      // ----- textarea -----
      return (
        <textarea
          {...common}
          className="custom-input w-full appearance-none resize-y min-h-[200px] p-2"
        />
      );

    default:
      // ----- text (default) -----
      return <input type="text" {...common} />;
  }
}
